import React, { useCallback, useReducer, useContext, useState, useEffect } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	IEditRestaurantContext,
	EditRestaurantContext,
} from '../../../../../editRestaurantState/editRestaurantContext';
import { ReportsContainerActionTypes } from './reportsContainerActions';
import { reportsContainerReducer } from './reportsContainerReducer';
import {
	reportsContainerInitialState,
	ReportsContainerContext,
	TTypeOfModalWindow,
	CREATE_REPORT,
	NEW_EMAIL_PREFIX,
	INCOMING_EMAIL_PREFIX,
	NEW_COPY_EMAIL_PREFIX,
	INCOMING_COPY_EMAIL_PREFIX,
} from './reportsContainerContext';
import { IGetRestaurantReportsInfo } from 'types';
import { TIPS_JAR_CLIENT_TYPE } from 'appConstants';
import { getRestaurantReportsInfo, deleteRestaurantReports } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';

export const ReportsContainerState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(reportsContainerReducer, reportsContainerInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { restaurantId, visibleForm, clientType } = useContext<IEditRestaurantContext>(EditRestaurantContext);
	const [isFirstEntry, setIsFirstEntry] = useState(true);
	const needStyleDataRequest = restaurantId && visibleForm === 'reportsForm';

	const { response, loading, refetch } = useFetchData<IGetRestaurantReportsInfo, string>(
		needStyleDataRequest ? getRestaurantReportsInfo : null,
		restaurantId || '',
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (isFirstEntry) {
			refetch();
		}
	}, [restaurantId, visibleForm]);

	useEffect(() => {
		if (response?.data.reports.length) {
			dispatch({
				type: ReportsContainerActionTypes.SET_REPORTS,
				payload: {
					reports: response.data.reports.map((report) => ({
						restaurantId,
						id: report.id,
						IDReportType: report.reportType,
						IDsScheduleTypes: report.scheduleTypes,
						emails: report.emails.length
							? report.emails.map((el, i) => ({
									id: `${INCOMING_EMAIL_PREFIX}/${String(i)}`,
									email: el,
							  }))
							: [{ id: NEW_EMAIL_PREFIX, email: '' }],
						copyEmails: report.copyEmails.length
							? report.copyEmails.map((el, i) => ({
									id: `${INCOMING_COPY_EMAIL_PREFIX}/${String(i)}`,
									email: el,
							  }))
							: [{ id: NEW_COPY_EMAIL_PREFIX, email: '' }],
						sendingReportAfterShiftClosing: report.isShiftsWorking,
					})),
				},
			});
			setIsFirstEntry(false);
		}
	}, [response]);

	const addNewReport = useCallback(() => {
		dispatch({
			type: ReportsContainerActionTypes.SET_REPORTS,
			payload: {
				reports: [
					...state.reports,
					{
						restaurantId,
						id: CREATE_REPORT,
						IDReportType: clientType === TIPS_JAR_CLIENT_TYPE ? 'Detailed' : null,
						IDsScheduleTypes: [],
						emails: [{ id: NEW_EMAIL_PREFIX, email: '' }],
						copyEmails: [{ id: NEW_COPY_EMAIL_PREFIX, email: '' }],
						sendingReportAfterShiftClosing: false,
					},
				],
			},
		});
	}, [state.reports, restaurantId, clientType]);

	const deleteReport = useCallback(
		async (reportID: string) => {
			try {
				showLoader();
				await deleteRestaurantReports(reportID);
				const filteredReports = state.reports.filter((report) => report.id !== reportID);
				dispatch({
					type: ReportsContainerActionTypes.SET_REPORTS,
					payload: {
						reports: filteredReports,
					},
				});
			} catch (err) {
				dispatch({
					type: ReportsContainerActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'deletionError',
					},
				});
			} finally {
				hideLoader();
			}
		},
		[[state.reports]],
	);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ReportsContainerActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<ReportsContainerContext.Provider
			value={{
				addNewReport,
				deleteReport,
				changeTypeOfModalWindow,
				reports: state.reports,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ReportsContainerContext.Provider>
	);
};
